import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";
import { fmtDateIS } from "@/utils/formatDate";

// Afsláttur (Car Loans) Component - Similar to Applications but for cars
export const Afslattur = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchKennitala, setSearchKennitala] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/loans`);
      // Filter only car-related loans (you can add a specific field like 'is_car_loan' to differentiate)
      // For now, we'll show all loans but you can filter based on loan_type or add a new field
      setApplications(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Villa við að sækja bifreiðalán");
      setLoading(false);
    }
  };

  // Filter by status
  const filteredByStatus = statusFilter === "all" 
    ? applications 
    : applications.filter(app => app.status === statusFilter);

  // Filter by kennitala search
  const filteredApplications = filteredByStatus.filter(app => {
    const kennitalMatch = searchKennitala.trim()
      ? app.borrower_info?.ssn?.includes(searchKennitala.trim())
      : true;
    return kennitalMatch;
  });

  if (loading) {
    return <div className="p-8">Hleður...</div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Car className="h-8 w-8" />
            Afsláttur - Bifreiðalán
          </h1>
          <p className="text-gray-600 mt-2">Stjórna bifreiðalánum og tilboðum</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("all")}
                >
                  Allar umsóknir
                </Button>
                <Button
                  variant={statusFilter === "pending" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("pending")}
                >
                  Í bið
                </Button>
                <Button
                  variant={statusFilter === "approved" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("approved")}
                >
                  Samþykkt
                </Button>
                <Button
                  variant={statusFilter === "disbursed" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("disbursed")}
                >
                  Virk
                </Button>
              </div>
              <div className="ml-auto">
                <Input
                  placeholder="Leita eftir kennitölu"
                  value={searchKennitala}
                  onChange={(e) => setSearchKennitala(e.target.value)}
                  className="w-64"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nafn</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kennitala</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upphæð</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tegund</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staða</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dagsetning</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aðgerðir</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        Engar umsóknir fundust
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{app.borrower_info?.full_name}</td>
                        <td className="px-4 py-3 text-sm">{app.borrower_info?.ssn}</td>
                        <td className="px-4 py-3 text-sm">{formatIcelandicNumber(app.loan_amount)} kr</td>
                        <td className="px-4 py-3 text-sm">{getLoanTypeLabel(app.loan_type)}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              app.status === "disbursed" ? "default" :
                              app.status === "approved" ? "secondary" :
                              app.status === "pending" ? "outline" :
                              "destructive"
                            }
                          >
                            {app.status === "disbursed" ? "Virk" :
                             app.status === "approved" ? "Samþykkt" :
                             app.status === "pending" ? "Í bið" :
                             app.status === "closed" ? "Lokið" :
                             app.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {fmtDateIS(app.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/applications`)}
                            title="Skoða í Umsóknum"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Um Afsláttur - Bifreiðalán</h3>
                <p className="text-sm text-blue-800 mt-1">
                  Þessi síða sýnir bifreiðalán og sérstök tilboð. Til að búa til nýtt bifreiðalán, farðu í "Umsóknir" og veldu viðeigandi lánategund.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

